import styles from './Logo.module.css';
import mark from './logoMark.json';

export interface LogoProps {
    /** Hides the wordmark, leaving only the mark. Used in tight headers. */
    markOnly?: boolean;
    /** `md` is the base size and needs no modifier class. */
    size?: 'sm' | 'md' | 'lg';
    /**
     * `onDark` for surfaces that stay dark in *both* themes — the marketing
     * panel on the auth screens, the workout player. Without it the mark and
     * wordmark follow the theme and disappear into a dark background whenever
     * the user is in light mode.
     */
    tone?: 'auto' | 'onDark';
}

/**
 * The ThuisCoach mark: a roof line over a dumbbell — home plus training.
 *
 * The geometry comes from `logoMark.json` rather than living here, because the
 * favicon, the installed-app icons and the mark the mail carries all have to be
 * the same shape and cannot read a CSS variable. They are generated from that
 * file by `scripts/generate-app-icons.mjs`; this component renders it live so
 * it still inherits the theme through design tokens. The stroke widths and the
 * roof's opacity are attributes here for the same reason — split between the
 * JSON and the stylesheet, they would drift.
 */
export function Logo({ markOnly = false, size = 'md', tone = 'auto' }: LogoProps) {
    const classes = [
        styles.logo,
        size === 'sm' ? styles.sm : null,
        size === 'lg' ? styles.lg : null,
        tone === 'onDark' ? styles.onDark : null,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <span className={classes}>
            <svg className={styles.mark} viewBox={mark.viewBox} role="img" aria-label="ThuisCoach">
                <rect width="32" height="32" rx={mark.plateRadius} className={styles.plate} />
                <path
                    d={mark.roof.d}
                    className={styles.roof}
                    fill="none"
                    strokeWidth={mark.roof.strokeWidth}
                    opacity={mark.roof.opacity}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d={mark.bar.d}
                    className={styles.bar}
                    fill="none"
                    strokeWidth={mark.bar.strokeWidth}
                    strokeLinecap="round"
                />
            </svg>
            {!markOnly && (
                <span className={styles.wordmark}>
                    thuis<span className={styles.wordmarkAccent}>coach</span>
                </span>
            )}
        </span>
    );
}
